import { addMinutes, differenceInMinutes } from "date-fns";

import {
  PopupAppointmentEntry,
  CalendarEntryTmp,
  CalendarEntryType,
  CalendarRelativeLocation,
  CalendarResourceWithType,
} from "frontend/interfaces/calendar";
import { AppointmentType } from "frontend/interfaces/appointment-type";
import { Interval } from "shared/interfaces/interval";
import { Patient } from "frontend/interfaces/patient";
import { Person } from "frontend/interfaces/person";
import { TmpUnit } from "frontend/interfaces/unit";
import {
  newClickUnit,
  unitNameOnlyForQuickCreate,
  unitsForQuickCreate,
} from "frontend/parser/parse-unit";
import {
  dateFromIntervalAndLocation,
  heightPercentage,
  higherOrderIsInInterval,
  topOffsetPercentage,
} from "frontend/utils/base-calendar-utils";
import { ConstantsDayView } from "frontend/utils/constants";
import { createAppointment } from "frontend/api/application/request-appointments";
import { createUnit } from "frontend/api/application/request-unit";
import {
  AbstractViewCellPosition,
  AbstractViewOptions,
  Database,
} from "frontend/uses/abstract-view/use-abstract-view-parser";
import { profiledCalendarConstants } from "frontend/utils/profile-helper";
import { PrimaryKey } from "frontend/interfaces/primary-key";
import { pullOnce, putOnce } from "frontend/uses/use-transience";
import { Room } from "frontend/interfaces/room";
import { getResource } from "frontend/uses/abstract-view/entries/units";

import BCTmpComponent from "frontend/components/base-calendar/BCTmp.vue";

export function entriesTmpUnits(
  resource: CalendarResourceWithType,
  interval: Interval,
  columnInterval: Interval,
  cellPosition: AbstractViewCellPosition,
  database: Database,
  options: AbstractViewOptions
): Array<CalendarEntryTmp> {
  const { room, person } = getResource(resource);

  const matches =
    database.clickUnit &&
    ((database.clickUnit.resourceType === "person" &&
      resource.type === "person" &&
      database.clickUnit.person_id === resource.id) ||
      (database.clickUnit.resourceType === "room" &&
        resource.type === "room" &&
        database.clickUnit.room_id === resource.id));

  const tmpUnits = matches ? [database.clickUnit] : [];

  return tmpUnits
    .filter(
      (candidate): candidate is NonNullable<typeof database.clickUnit> =>
        !!candidate
    )
    .filter(
      higherOrderTmpUnitIsInInterval(interval, columnInterval, cellPosition)
    )
    .map((tmpUnit) =>
      tmpUnitToEntry(
        tmpUnit,
        person,
        room,
        interval,
        columnInterval,
        cellPosition,
        database,
        options
      )
    );
}

// can be used as replacement to tmpUnitToEntry
// if there is no database or abstract view involved
// and it is only a PopupAppointment needed
export function tmpUnitToPopupAppointmentEntry(
  tmpUnit: TmpUnit
): PopupAppointmentEntry {
  return {
    key: tmpUnit.key,
    unit: tmpUnit,

    doDestroy: () => {
      return;
    },
    doSubmit: async (
      units: TmpUnit[],
      patient: Patient,
      appointmentType: AppointmentType,
      orderTime: number,
      notifyPatient: boolean,
      remindPatient: boolean
    ) => {
      return await createAppointment(
        unitsForQuickCreate(
          units,
          patient,
          appointmentType,
          orderTime,
          notifyPatient,
          remindPatient
        )
      );
    },
    doSubmitUnit: async () => {
      return await createUnit(unitNameOnlyForQuickCreate(tmpUnit));
    },

    onPersonChange: (newPersonId: PrimaryKey) => {
      tmpUnit.person_id = newPersonId;
    },
    onRoomChange: (newRoomId: PrimaryKey) => {
      tmpUnit.room_id = newRoomId;
    },

    onDateChange: (from: Date, to: Date) => {
      tmpUnit.from = from;
      tmpUnit.to = to;
    },
  };
}

export function tmpUnitToEntry(
  tmpUnit: TmpUnit,
  person: Person | null,
  room: Room | null,
  interval: Interval,
  columnInterval: Interval,
  cellPosition: AbstractViewCellPosition,
  database: Database,
  options: AbstractViewOptions
): CalendarEntryTmp {
  return {
    key: tmpUnit.key,
    componentCalendar: () => BCTmpComponent,

    topOffsetPercentage: topOffsetPercentage(
      tmpUnit.visual.from,
      interval.from,
      cellPosition,
      profiledCalendarConstants(ConstantsDayView)
    ),
    heightPercentage: heightPercentage(
      tmpUnit.visual,
      profiledCalendarConstants(ConstantsDayView)
    ),

    type: CalendarEntryType.Tmp,
    doDestroy: () => {
      updateClickUnit(
        null,
        person,
        room,
        interval,
        columnInterval,
        database,
        options
      );
    },
    doSubmit: async (
      units: TmpUnit[],
      patient: Patient,
      appointmentType: AppointmentType,
      orderTime: number,
      notifyPatient: boolean,
      remindPatient: boolean
    ) => {
      return await createAppointment(
        unitsForQuickCreate(
          units,
          patient,
          appointmentType,
          orderTime,
          notifyPatient,
          remindPatient
        )
      );
    },
    doSubmitUnit: async () => {
      return await createUnit(unitNameOnlyForQuickCreate(tmpUnit));
    },

    onPersonChange: (newPersonId: PrimaryKey) => {
      tmpUnit.person_id = newPersonId;

      if (database.persons?.map((p) => p.id).indexOf(newPersonId) >= 0) return;
      else {
        putOnce("abstract-view__clickUnit", tmpUnit);
        database.clickUnit = setupClickUnit();
      }
    },

    onRoomChange: (newRoomId: PrimaryKey) => {
      tmpUnit.room_id = newRoomId;

      if (database.rooms?.map((r) => r.id).indexOf(newRoomId) >= 0) return;
      else {
        putOnce("abstract-view__clickUnit", tmpUnit);
        database.clickUnit = setupClickUnit();
      }
    },
    onDateChange: (from: Date, to: Date) => {
      tmpUnit.from = from;
      tmpUnit.to = to;
    },

    unit: tmpUnit,
  };
}

function higherOrderTmpUnitIsInInterval(
  interval: Interval,
  columnInterval: Interval,
  cellPosition: AbstractViewCellPosition
): (tmpUnit: TmpUnit) => boolean {
  return (tmpUnit: TmpUnit) =>
    higherOrderIsInInterval(
      interval,
      columnInterval,
      cellPosition
    )(tmpUnit.from, tmpUnit.to);
}

export function onClickCreateClickUnit(
  resource: CalendarResourceWithType,
  interval: Interval,
  columnInterval: Interval,
  database: Database,
  options: AbstractViewOptions
): (location: CalendarRelativeLocation) => void {
  return (location: CalendarRelativeLocation) => {
    const clickedDate = dateFromIntervalAndLocation(
      interval,
      location,
      profiledCalendarConstants(ConstantsDayView).ROUND_CLICK_TO_NEAREST
    );
    const { room, person, personId, roomId } = getResource(resource);

    const clickUnit = database.clickUnit ?? newClickUnit(personId, roomId);

    const unitDurationInMinutes = differenceInMinutes(
      clickUnit.to,
      clickUnit.from
    );
    clickUnit.from = clickedDate;
    clickUnit.to = addMinutes(clickedDate, unitDurationInMinutes);
    clickUnit.person_id = personId;
    clickUnit.room_id = roomId;
    clickUnit.resourceType = resource.type;
    updateClickUnit(
      clickUnit,
      person,
      room,
      interval,
      columnInterval,
      database,
      options
    );
  };
}

function updateClickUnit(
  clickUnit: TmpUnit | null,
  _person: Person | null,
  _room: Room | null,
  _interval: Interval,
  _columnInterval: Interval,
  database: Database,
  _options: AbstractViewOptions
): void {
  database.clickUnit = clickUnit;
}

export function setupClickUnit(): TmpUnit | null {
  return pullOnce("abstract-view__clickUnit") ?? null;
}
